import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { FormattedMessage } from 'react-intl';

import {
  ConfirmationModal,
  LoadingPane,
} from '@folio/stripes/components';
import { stripesConnect } from '@folio/stripes/core';

import { flatten } from 'lodash';
import { EntityNotFound } from '../../../routes/components';
import { TeamView } from '../../components/team/team-view';

import {
  SETTINGS_DETAIL_VIEW_PANE_WIDTH,
  TEAM_FIELDS,
  LOAD_LIMIT,
  LOAD_LIMIT_MAX,
  getTeamEditUrl,
  getTeamListUrl,
} from '../../../constants';
import { useCallout } from '../../../hooks';

const TeamViewRoute = ({
  history,
  location,
  mutator,
  match: { params: { id } },
}) => {
  const [isConfirmDeleteModalOpen, setConfirmModalOpen] = useState(false);
  const [team, setTeam] = useState({});
  const [isTeamLoaded, setIsTeamLoaded] = useState(false);
  const [isTeamFailed, setIsTeamFailed] = useState(false);

  const [users, setUsers] = useState([]);

  const showCallout = useCallout();

  useEffect(
    () => {
      let isSubscribed = true;

      setIsTeamLoaded(false);
      setIsTeamFailed(false);
      mutator.teams.GET({
        params: {
          limit: LOAD_LIMIT_MAX,
        },
      })
        .then((teamResponse) => {
          if (isSubscribed) {
            setTeam(teamResponse);

            const userIds = teamResponse.members || [];
            const usersPromises = [];

            while (userIds.length) {
              usersPromises.push(mutator.users.GET({
                params: {
                  limit: LOAD_LIMIT,
                  query: userIds.splice(0, LOAD_LIMIT)
                    .map(userId => `id==${userId}`)
                    .join(' or '),
                }
              }));
            }

            return Promise.all(usersPromises)
              .then((userArrays) => {
                const combinedUsers = flatten(userArrays.map((arr) => arr.users));

                setUsers(combinedUsers);
              });
          } else {
            return Promise.all([]);
          }
        })
        .catch(() => {
          if (isSubscribed) {
            setIsTeamFailed(true);
          }
        })
        .then(() => {
          if (isSubscribed) {
            setIsTeamLoaded(true);
          }
        });

      return () => {
        isSubscribed = false;
      };
    },
    [id]
  );

  const onBack = useCallback(() => {
    history.push({
      pathname: getTeamListUrl(),
      search: location.search,
    });
  }, [location.search, history]);

  const navigateToEdit = useCallback(() => {
    history.push(getTeamEditUrl(id, location.search));
  }, [location.search, history, id]);

  const handleDeleteConfirmationModal = () => setConfirmModalOpen(!isConfirmDeleteModalOpen);

  const handleDelete = () => {
    setConfirmModalOpen(false);
    mutator.teams.DELETE({ id })
      .then(() => {
        showCallout({
          message: <FormattedMessage
            id="ui-task-list.callout.deleted.team"
            values={{ name: team[TEAM_FIELDS.NAME] }}
          />
        });
        history.replace({
          pathname: getTeamListUrl(),
          search: location.search,
        });
      })
      .catch(() => {
        showCallout({
          type: 'error',
          message: <FormattedMessage id="ui-task-list.validation.communicationProblem" />,
        });
      });
  };

  if (isTeamFailed) {
    return (
      <EntityNotFound
        errorTextTranslationKey="ui-task-list.error.notFound.team"
        pageTitleTranslationKey="ui-task-list.title.view.team"
        onBack={onBack}
      />
    );
  }

  if (!isTeamLoaded) {
    return (
      <LoadingPane
        dismissible
        defaultWidth={SETTINGS_DETAIL_VIEW_PANE_WIDTH}
        paneTitle={<FormattedMessage id="ui-task-list.title.view.team" />}
        onClose={onBack}
      />);
  }

  return (
    <>
      <TeamView
        team={team}
        users={users}
        onBack={onBack}
        onDelete={handleDeleteConfirmationModal}
        onEdit={navigateToEdit}
      />
      <ConfirmationModal
        cancelLabel={<FormattedMessage id="ui-task-list.confirmationDeleteModal.cancel" />}
        confirmLabel={<FormattedMessage id="ui-task-list.confirmationDeleteModal.confirm" />}
        heading={<FormattedMessage id="ui-task-list.confirmationDeleteModal.heading.team" />}
        id="confirm-delete-team-action"
        message={<FormattedMessage id="ui-task-list.confirmationDeleteModal.message.team" />}
        open={isConfirmDeleteModalOpen}
        onCancel={handleDeleteConfirmationModal}
        onConfirm={handleDelete}
      />
    </>
  );
};

TeamViewRoute.manifest = Object.freeze({
  teams: {
    type: 'okapi',
    path: 'tasks/teams/:{id}',
    clientGeneratePk: false,
    throwErrors: false,
    accumulate: true,
    fetch: false,
    DELETE: {
      path: 'tasks/teams/:{id}',
    },
  },
  users: {
    type: 'okapi',
    path: 'users',
    clientGeneratePk: false,
    throwErrors: false,
    accumulate: true,
    fetch: false,
  },
});

TeamViewRoute.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
  match: ReactRouterPropTypes.match.isRequired,
  mutator: PropTypes.shape({
    teams: PropTypes.shape({
      DELETE: PropTypes.func.isRequired,
      GET: PropTypes.func.isRequired,
    }),
    users: PropTypes.shape({
      GET: PropTypes.func.isRequired,
    }),
  }),
};

export default stripesConnect(TeamViewRoute);
